import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { useNavigate, useParams } from 'react-router-dom';
import { useToast } from '../../context/ToastContext';

export default function EmployeeEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [stillWorking, setStillWorking] = useState(false);

  const [passwordForm, setPasswordForm] = useState({ newPassword: '', confirmPassword: '' });
  const [resettingPassword, setResettingPassword] = useState(false);

  const [form, setForm] = useState({
    nip: '',
    noNik: '',
    firstName: '',
    lastName: '',
    email: '',
    dateStartWork: '',
    dateEndWork: '',
    linkedIn: '',
    fileCv: '',
    systemRole: 'STAFF',
  });

  useEffect(() => {
    const fetchEmployee = async () => {
      try {
        const data = await api.getEmployeeDetail({ id });
        setForm({
          nip: data.nip || '',
          noNik: data.noNik || '',
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          email: data.email || '',
          dateStartWork: data.dateStartWorkRaw || '',
          dateEndWork: data.dateEndWorkRaw || '',
          linkedIn: data.linkedIn || '',
          fileCv: data.urlFileCv || '',
          systemRole: data.systemRole || 'STAFF',
        });
        setStillWorking(!data.dateEndWorkRaw);
      } catch (err) {
        console.error(err);
        navigate('/employee', {
          state: {
            toastMessage: 'Gagal memuat data employee.',
            toastType: 'danger',
          },
        });
      } finally {
        setLoading(false);
      }
    };
    fetchEmployee();
  }, [id, navigate]);

  const handleChange = (e) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value,
    });
  };

  const handleStillWorking = () => {
    setStillWorking(!stillWorking);
    setForm({
      ...form,
      dateEndWork: stillWorking ? '' : null,
    });
  };

  const validate = () => {
    if (!form.noNik || !form.firstName || !form.lastName) {
      showToast('Field bertanda * wajib diisi.', 'danger');
      return false;
    }

    if (form.fileCv && !form.fileCv.startsWith('https://drive.google.com/')) {
      showToast('CV harus berupa link Google Drive yang valid.', 'danger');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      setSubmitting(true);

      const payload = {
        id,
        nip: form.nip || '',
        noNik: form.noNik,
        firstName: form.firstName,
        lastName: form.lastName,
        dateStartWork: form.dateStartWork || null,
        dateEndWork: stillWorking ? null : form.dateEndWork || null,
        linkedIn: form.linkedIn || '',
        fileCv: form.fileCv || '',
        systemRole: form.systemRole,
      };

      await api.updateEmployee(payload);

      showToast('Data employee berhasil diperbarui!', 'success');
      navigate(`/employee/${id}`);
    } catch (err) {
      console.error(err);
      showToast('Terjadi kesalahan saat memperbarui data.', 'danger');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePasswordChange = (e) => {
    setPasswordForm({
      ...passwordForm,
      [e.target.name]: e.target.value,
    });
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (passwordForm.newPassword.length < 8) {
      showToast('Password baru minimal 8 karakter.', 'danger');
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      showToast('Konfirmasi password tidak sama.', 'danger');
      return;
    }

    try {
      setResettingPassword(true);
      await api.resetEmployeePassword({ id, newPassword: passwordForm.newPassword });
      showToast('Password employee berhasil direset!', 'success');
      setPasswordForm({ newPassword: '', confirmPassword: '' });
    } catch (err) {
      console.error(err);
      showToast(err.message || 'Terjadi kesalahan saat mereset password.', 'danger');
    } finally {
      setResettingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border text-primary" role="status"></div>
        <p className="text-muted mt-3">Memuat data employee...</p>
      </div>
    );
  }

  return (
    <div className="container py-4" style={{ maxWidth: '720px' }}>
      {/* Back Button */}
      <div className="mb-3">
        <button
          type="button"
          className="btn btn-white btn-sm rounded-circle shadow-sm border bg-white text-dark d-inline-flex align-items-center justify-content-center"
          style={{ width: '38px', height: '38px' }}
          onClick={() => navigate(-1)}
        >
          <i className="bi bi-arrow-left fs-5"></i>
        </button>
      </div>

      {/* HEADER */}
      <div className="mb-4">
        <h3 className="fw-bold">Edit Data Employee</h3>
        <p className="text-muted">
          Perbarui data karyawan di bawah ini. Field bertanda <span className="text-danger">*</span> wajib diisi.
        </p>
      </div>

      <div className="card border-0 shadow-sm rounded-4">
        <div className="card-body p-4">
          <form onSubmit={handleSubmit} className="row g-3">
            {/* Email (Readonly) */}
            <div className="col-12">
              <label className="form-label fw-semibold">Email Karyawan</label>
              <input
                type="email"
                className="form-control rounded-3 bg-light text-muted"
                value={form.email}
                disabled
              />
              <div className="form-text">Email tidak dapat diubah di sini.</div>
            </div>

            {/* Nama Depan */}
            <div className="col-md-6">
              <label className="form-label fw-semibold">
                Nama Depan <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control rounded-3"
                name="firstName"
                required
                value={form.firstName}
                onChange={handleChange}
              />
            </div>

            {/* Nama Belakang */}
            <div className="col-md-6">
              <label className="form-label fw-semibold">
                Nama Belakang <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control rounded-3"
                name="lastName"
                required
                value={form.lastName}
                onChange={handleChange}
              />
            </div>

            {/* NIP */}
            <div className="col-md-6">
              <label className="form-label fw-semibold">NIP</label>
              <input
                type="text"
                className="form-control rounded-3"
                name="nip"
                value={form.nip}
                onChange={handleChange}
              />
            </div>

            {/* NIK */}
            <div className="col-md-6">
              <label className="form-label fw-semibold">
                NIK <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control rounded-3"
                name="noNik"
                required
                value={form.noNik}
                onChange={handleChange}
              />
            </div>

            {/* System Role */}
            <div className="col-12">
              <label className="form-label fw-semibold">
                Peran Sistem (System Role) <span className="text-danger">*</span>
              </label>
              <select
                className="form-select rounded-3"
                name="systemRole"
                value={form.systemRole}
                onChange={handleChange}
                required
              >
                <option value="STAFF">Staff (Default)</option>
                <option value="MANAGER">Manager</option>
              </select>
            </div>

            {/* Status Kerja Switch */}
            <div className="col-12">
              <div className="card bg-light border-0 rounded-4 p-3 mb-2">
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <h6 className="fw-bold mb-1">
                      <i className="bi bi-briefcase-fill text-primary me-2"></i>Status Karyawan Aktif
                    </h6>
                    <p className="text-muted small mb-0">Aktifkan switch ini jika karyawan saat ini masih bekerja aktif di sini.</p>
                  </div>
                  <div className="form-check form-switch fs-4">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      checked={stillWorking}
                      onChange={handleStillWorking}
                      id="stillWorking"
                      style={{ cursor: 'pointer' }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Start Work */}
            <div className="col-md-6">
              <label className="form-label fw-semibold">
                Tanggal Mulai Kerja
              </label>
              <input
                type="date"
                className="form-control rounded-3"
                name="dateStartWork"
                value={form.dateStartWork}
                onChange={handleChange}
              />
            </div>

            {/* End Work */}
            <div className="col-md-6">
              <label className="form-label fw-semibold">
                Tanggal Akhir Kerja
              </label>
              <input
                type="date"
                className="form-control rounded-3"
                name="dateEndWork"
                disabled={stillWorking}
                value={stillWorking ? '' : (form.dateEndWork || '')}
                onChange={handleChange}
                placeholder="Pilih tanggal jika sudah resign"
              />
            </div>

            {/* LinkedIn */}
            <div className="col-12">
              <label className="form-label fw-semibold">Link LinkedIn</label>
              <input
                type="text"
                className="form-control rounded-3"
                name="linkedIn"
                value={form.linkedIn}
                onChange={handleChange}
                placeholder="https://linkedin.com/in/username"
              />
            </div>

            {/* File CV */}
            <div className="col-12">
              <label className="form-label fw-semibold">Link Google Drive CV</label>
              <input
                type="text"
                className="form-control rounded-3"
                name="fileCv"
                value={form.fileCv}
                onChange={handleChange}
                placeholder="https://drive.google.com/..."
              />
            </div>

            {/* Buttons */}
            <div className="d-flex justify-content-end mt-4">
              <button
                type="button"
                className="btn btn-light me-2 rounded-pill"
                onClick={() => navigate(`/employee/${id}`)}
                disabled={submitting}
              >
                Batal
              </button>

              <button
                type="submit"
                className="btn btn-primary rounded-pill px-4"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                    ></span>
                    Menyimpan...
                  </>
                ) : (
                  <>
                    <i className="bi bi-save me-2"></i>
                    Simpan Perubahan
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* RESET PASSWORD */}
      <div className="card border-0 shadow-sm rounded-4 mt-4">
        <div className="card-body p-4">
          <h5 className="fw-bold mb-1">
            <i className="bi bi-key-fill text-primary me-2"></i>Reset Password
          </h5>
          <p className="text-muted small mb-3">
            Set password baru untuk employee ini. Employee akan login menggunakan password baru ini.
          </p>

          <form onSubmit={handleResetPassword} className="row g-3">
            <div className="col-md-6">
              <label className="form-label fw-semibold">Password Baru</label>
              <input
                type="password"
                className="form-control rounded-3"
                name="newPassword"
                required
                minLength={8}
                value={passwordForm.newPassword}
                onChange={handlePasswordChange}
                placeholder="Minimal 8 karakter"
              />
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold">Konfirmasi Password Baru</label>
              <input
                type="password"
                className="form-control rounded-3"
                name="confirmPassword"
                required
                minLength={8}
                value={passwordForm.confirmPassword}
                onChange={handlePasswordChange}
                placeholder="Ulangi password baru"
              />
            </div>

            <div className="col-12 d-flex justify-content-end">
              <button
                type="submit"
                className="btn btn-outline-primary rounded-pill px-4"
                disabled={resettingPassword}
              >
                {resettingPassword ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                    ></span>
                    Mereset...
                  </>
                ) : (
                  <>
                    <i className="bi bi-key me-2"></i>
                    Reset Password
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
